import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../utils/firebase";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  Activity,
  AlertTriangle,
  Brain,
  Info,
  Users,
  Heart,
  Home,
  ChevronDown,
} from "lucide-react";

const RCProgressionTab = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState("recoveryCapitalTotal");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [relapseDistributionData, setRelapseDistributionData] = useState({});
  const [totalRelapsesCount, setTotalRelapsesCount] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState("relapse"); // "relapse" or "rcScore"
  const [isMetricDropdownOpen, setIsMetricDropdownOpen] = useState(false);
  const [individualTrajectories, setIndividualTrajectories] = useState([]);
  const [growthRates, setGrowthRates] = useState({});
  const [showTrajectories, setShowTrajectories] = useState(false);
  const [selectedRiskFactor, setSelectedRiskFactor] = useState("mat");
  const [isRiskFactorDropdownOpen, setIsRiskFactorDropdownOpen] =
    useState(false);
  const [riskFactorData, setRiskFactorData] = useState({});
  const [domainDescriptions, setDomainDescriptions] = useState({});
  const [progressionData, setProgressionData] = useState([]);
  const [domainProgressionData, setDomainProgressionData] = useState({});
  const [survivalData, setSurvivalData] = useState([]);

  // Helper to format days into months and days
  const formatDaysToMonthsAndDays = (days) => {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;

    if (months === 0) {
      return `${days} days`;
    } else if (remainingDays === 0) {
      return months === 1 ? `1 month` : `${months} months`;
    } else {
      return `${months}m ${remainingDays}d`;
    }
  };

  // Helper to format timePoint strings
  const formatTimePoint = (timePoint) => {
    switch (timePoint) {
      case "0-90 days":
        return "0-3m";
      case "91-180 days":
        return "3-6m";
      case "181-270 days":
        return "6-9m";
      case "271-365 days":
        return "9-12m";
      case "366-540 days":
        return "12-18m";
      case "541-730 days":
        return "18-24m";
      case "731+ days":
        return "24m+";
      default:
        return timePoint;
    }
  };

  // Domain configurations
  const domainOptions = [
    { id: "recoveryCapitalTotal", name: "Total RC Score", color: "#3b82f6" },
    {
      id: "spiritualCapitalScore",
      name: "Spiritual Capital",
      color: "#8B5CF6",
      icon: Heart,
    },
    {
      id: "socialCapitalScore",
      name: "Social Capital",
      color: "#3B82F6",
      icon: Users,
    },
    {
      id: "individualCapitalScore",
      name: "Individual Capital",
      color: "#10B981",
      icon: Brain,
    },
    {
      id: "physicalCapitalScore",
      name: "Physical Capital",
      color: "#F59E0B",
      icon: Activity,
    },
    {
      id: "generalCapitalScore",
      name: "General Capital",
      color: "#EC4899",
      icon: Home,
    },
  ];

  // Risk factor configurations
  const riskFactorOptions = [
    {
      id: "mat",
      name: "Medication-Assisted Treatment",
      yesLabel: "With MAT",
      noLabel: "Without MAT",
    },
    {
      id: "trauma",
      name: "Childhood Trauma",
      yesLabel: "With Trauma",
      noLabel: "No Trauma",
    },
    {
      id: "homeless",
      name: "Homeless more than a Year",
      yesLabel: "Previously Homeless",
      noLabel: "Not Previously Homeless",
    },
    {
      id: "prison",
      name: "Prison History",
      yesLabel: "Prison History",
      noLabel: "No Prison History",
    },
    {
      id: "diagnosis",
      name: "Co-occurring Diagnosis",
      yesLabel: "With Co-occurring",
      noLabel: "Without Co-occurring",
    },
    {
      id: "kids",
      name: "Has Children",
      yesLabel: "Has Children",
      noLabel: "No Children",
    },
    {
      id: "kidsRelation",
      name: "Relationship with Children",
      yesLabel: "Strong Relationship",
      noLabel: "Weak Relationship",
    },
    {
      id: "therapy",
      name: "Therapy",
      yesLabel: "In Therapy",
      noLabel: "Without Therapy",
    },
  ];

  const metricOptions = [
    {
      id: "relapse",
      name: "Relapse %",
      yAxisLabel: "Relapse Percentage",
      color: "#3B82F6",
    },
    {
      id: "rcScore",
      name: "RC Score",
      yAxisLabel: "Recovery Capital Score",
      color: "#10B981",
    },
  ];

  const generateDomainDescription = (domainId, domainData) => {
    if (!domainData || domainData.length === 0) return "No data available";

    // Sort time points to ensure proper order for analysis
    const sortedData = [...domainData].sort((a, b) => {
      const timeOrder = {
        "0-90 days": 1,
        "91-180 days": 2,
        "181-270 days": 3,
        "271-365 days": 4,
        "366-540 days": 5,
        "541-730 days": 6,
        "731+ days": 7,
      };
      return timeOrder[a.timePoint] - timeOrder[b.timePoint];
    });

    // Get first and last valid data points to calculate overall change
    const firstPoint = sortedData.find((d) => d.score > 0);
    const lastPoint = [...sortedData].reverse().find((d) => d.score > 0);

    // Calculate average scores for different periods
    const earlyData = sortedData.slice(0, 2).filter((d) => d.score > 0);
    const midData = sortedData.slice(2, 4).filter((d) => d.score > 0);
    const lateData = sortedData.slice(4).filter((d) => d.score > 0);

    const earlyAvg = earlyData.length
      ? earlyData.reduce((sum, d) => sum + d.score, 0) / earlyData.length
      : 0;
    const midAvg = midData.length
      ? midData.reduce((sum, d) => sum + d.score, 0) / midData.length
      : 0;
    const lateAvg = lateData.length
      ? lateData.reduce((sum, d) => sum + d.score, 0) / lateData.length
      : 0;

    // Find peak score and when it occurs
    const peakData = sortedData.reduce(
      (max, d) => (d.score > max.score ? d : max),
      { score: 0 }
    );

    // Calculate overall change (start to end)
    const overallChange =
      firstPoint && lastPoint
        ? (lastPoint.score - firstPoint.score).toFixed(1)
        : 0;
    const overallChangePercent =
      firstPoint && lastPoint && firstPoint.score > 0
        ? (
            ((lastPoint.score - firstPoint.score) / firstPoint.score) *
            100
          ).toFixed(1)
        : 0;

    // Find if there's a plateau (difference between consecutive scores is less than 1.0)
    let plateauIndex = -1;
    for (let i = 1; i < sortedData.length; i++) {
      if (
        Math.abs(sortedData[i].score - sortedData[i - 1].score) < 1.0 &&
        sortedData[i].score > 0 &&
        sortedData[i - 1].score > 0
      ) {
        plateauIndex = i;
        break;
      }
    }

    // Check for dips (decrease of 1.0 or more after increasing)
    const dips = [];
    for (let i = 2; i < sortedData.length; i++) {
      if (
        sortedData[i].score < sortedData[i - 1].score &&
        sortedData[i - 1].score > sortedData[i - 2].score &&
        sortedData[i - 1].score - sortedData[i].score >= 1.0
      ) {
        dips.push({
          timePoint: sortedData[i].timePoint,
          amount: (sortedData[i - 1].score - sortedData[i].score).toFixed(1),
        });
      }
    }

    // Generate domain-specific descriptions
    switch (domainId) {
      case "spiritualCapitalScore": {
        // Find where plateau occurs if any
        const plateauTime =
          plateauIndex > 0
            ? formatTimePoint(sortedData[plateauIndex].timePoint)
            : "12-18m";

        // Compare with other domains to see if it's highest
        const allDomainAverages = {};
        Object.keys(domainProgressionData).forEach((id) => {
          if (
            id !== "recoveryCapitalTotal" &&
            domainProgressionData[id] &&
            domainProgressionData[id].length
          ) {
            const validScores = domainProgressionData[id]
              .filter((d) => d.score > 0)
              .map((d) => d.score);
            allDomainAverages[id] = validScores.length
              ? validScores.reduce((sum, score) => sum + score, 0) /
                validScores.length
              : 0;
          }
        });

        const isHighest =
          allDomainAverages[domainId] > 0 &&
          Object.entries(allDomainAverages).every(
            ([id, avg]) =>
              id === domainId || avg <= allDomainAverages[domainId] || avg === 0
          );

        return `${
          isHighest
            ? "Highest average score in the program"
            : "High average score"
        }; ${
          firstPoint && lastPoint
            ? `rises from ${firstPoint.score.toFixed(
                1
              )} to ${lastPoint.score.toFixed(1)};`
            : ""
        } plateaus around ${plateauTime}. ${
          isHighest ? "Most likely to be a person's strongest domain." : ""
        }`;
      }

      case "socialCapitalScore": {
        // Check for steady rise, dips, and late upticks
        const steadyRise = overallChange > 0;
        const hasDip = dips.length > 0;
        const latestData = sortedData.slice(-2).filter((d) => d.score > 0);
        const lateUptick =
          latestData.length === 2 && latestData[1].score > latestData[0].score;

        const startValue = firstPoint ? firstPoint.score.toFixed(1) : "N/A";
        const peakValue =
          peakData.score > 0 ? peakData.score.toFixed(1) : "N/A";

        return `Social Capital ${
          steadyRise ? "rises steadily" : "changes"
        } over the first 18 months (${startValue} → ${peakValue})${
          hasDip
            ? `, dips modestly in the ${formatTimePoint(
                dips[0].timePoint
              )} window`
            : ""
        }${lateUptick ? ", and trends upward again after two years" : ""}${
          lateUptick ? "—late uptick is provisional due to limited data." : "."
        }`;
      }

      case "individualCapitalScore": {
        // Check for consistency in improvement
        const steadyImprovement = sortedData.every(
          (d, i) =>
            i === 0 ||
            d.score >= sortedData[i - 1].score ||
            d.score === 0 ||
            sortedData[i - 1].score === 0
        );

        return `Acts as a 'hub domain' with strong connections to other dimensions. Shows ${
          steadyImprovement ? "steady" : "variable"
        } improvement over time${
          firstPoint && lastPoint
            ? ` (${firstPoint.score.toFixed(1)} → ${lastPoint.score.toFixed(
                1
              )})`
            : ""
        }.`;
      }

      case "physicalCapitalScore": {
        // Calculate early change vs late change
        const earlyChange = midAvg - earlyAvg;
        const lateChange = lateAvg - midAvg;
        const isLateImprovement = lateChange > earlyChange;

        return `Physical Capital shows ${
          Math.abs(earlyChange) < 1.5 ? "minimal" : "significant"
        } early change, adding only about ${
          Math.abs(overallChange) < 10 ? Math.abs(overallChange) : "three"
        } points overall (${
          firstPoint ? firstPoint.score.toFixed(1) : "N/A"
        } → ${lastPoint ? lastPoint.score.toFixed(1) : "N/A"}); ${
          isLateImprovement
            ? "most improvement appears after the first year."
            : "with consistent progression throughout."
        }`;
      }

      case "generalCapitalScore": {
        // Compare this domain with others to see if it's the lowest
        const allDomainAverages = {};
        Object.keys(domainProgressionData).forEach((id) => {
          if (
            id !== "recoveryCapitalTotal" &&
            domainProgressionData[id] &&
            domainProgressionData[id].length
          ) {
            const validScores = domainProgressionData[id]
              .filter((d) => d.score > 0)
              .map((d) => d.score);
            allDomainAverages[id] = validScores.length
              ? validScores.reduce((sum, score) => sum + score, 0) /
                validScores.length
              : 0;
          }
        });

        const isLowest =
          allDomainAverages[domainId] > 0 &&
          Object.entries(allDomainAverages).every(
            ([id, avg]) =>
              id === domainId || avg >= allDomainAverages[domainId] || avg === 0
          );

        return `${
          isLowest
            ? "Lowest average score among domains"
            : "Lower scoring domain"
        }, containing factors that require more time to change (recovery duration, step work progress, education level). Shows gradual improvement${
          overallChange > 0 ? ` of ${overallChange} points` : ""
        } as these time-dependent elements naturally progress.`;
      }

      default:
        return "Domain data analysis not available.";
    }
  };

  // Get the selected domain configuration
  const getSelectedDomainConfig = () => {
    return (
      domainOptions.find((domain) => domain.id === selectedDomain) ||
      domainOptions[0]
    );
  };

  useEffect(() => {
    const fetchProgressionData = async () => {
      try {
        if (!organizationId) throw new Error("Organization ID is required");
        setLoading(true);

        /* ──────────────────────────── fetch collections ───────────────────────────── */
        const [alumniSnapshot, residentSnapshot, rcSnapshot] =
          await Promise.all([
            getDocs(collection(db, `organizations/${organizationId}/alumni`)),
            getDocs(
              collection(db, `organizations/${organizationId}/residents`)
            ),
            getDocs(
              collection(db, `organizations/${organizationId}/recoveryCapital`)
            ),
          ]);
        if (rcSnapshot.empty) throw new Error("No recovery-capital data found");

        /* ────────────────────────────── helpers ───────────────────────────────────── */
        const getTimeGroupForDays = (days) => {
          if (days <= 90) return "0-90 days";
          if (days <= 180) return "91-180 days";
          if (days <= 270) return "181-270 days";
          if (days <= 365) return "271-365 days";
          if (days <= 540) return "366-540 days";
          if (days <= 730) return "541-730 days";
          return "731+ days";
        };

        const timePointBoundaries = {
          "0-90 days": { start: 0, end: 90 },
          "91-180 days": { start: 91, end: 180 },
          "181-270 days": { start: 181, end: 270 },
          "271-365 days": { start: 271, end: 365 },
          "366-540 days": { start: 366, end: 540 },
          "541-730 days": { start: 541, end: 730 },
          "731+ days": { start: 731, end: Infinity },
        };

        /* ───────────────────────────── time-group skeleton ────────────────────────── */
        const makeEmptyGroup = () => ({
          assessments: [],
          relapsed: 0, // Relapses *in this specific period*
          totalPeriodEntries: 0, // Total people counted in this period in the old method
          domains: {},
          atRisk: 0, // People who started this period sober (for survival)
          events: 0, // Relapses in this period (for survival)
          censored: 0, // People who left observation in this period (for survival)
          survivalProb: 100, // Cumulative survival probability
        });
        const timeGroups = {
          "0-90 days": makeEmptyGroup(),
          "91-180 days": makeEmptyGroup(),
          "181-270 days": makeEmptyGroup(),
          "271-365 days": makeEmptyGroup(),
          "366-540 days": makeEmptyGroup(),
          "541-730 days": makeEmptyGroup(),
          "731+ days": makeEmptyGroup(),
        };
        const domains = [
          "recoveryCapitalTotal",
          "spiritualCapitalScore",
          "socialCapitalScore",
          "individualCapitalScore",
          "physicalCapitalScore",
          "generalCapitalScore",
        ];
        Object.values(timeGroups).forEach((g) =>
          domains.forEach((d) => (g.domains[d] = []))
        );

        /* ───────────────────────────── Resident Profiles (for survival analysis) ───────────────────────── */
        const residentProfiles = new Map(); // residentId -> { sobrietyDate, eventDate, eventType ('relapse' | 'discharge' | 'active'), isMAT, hasTrauma, ... }

        // Process residents
        residentSnapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.sobrietyDate) {
            residentProfiles.set(d.id, {
              sobrietyDate: new Date(data.sobrietyDate),
              eventDate: new Date(), // Active residents are censored at current date
              eventType: "active",
              isMAT: data.isMAT === true,
              // Collect other risk factors
              childhoodTrauma: data.childhoodTrauma,
              homelessOneYear: data.homelessOneYear,
              overOneYearInPrison: data.overOneYearInPrison,
              coOccurringDiagnosis: data.coOccurringDiagnosis,
              kids: data.kids,
              strongRelationKids: data.strongRelationKids,
              therapy: data.therapy,
            });
          }
        });

        // Process alumni (overwriting if a resident record exists, or adding new)
        alumniSnapshot.docs.forEach((d) => {
          const data = d.data();
          const residentId = data.residentId || d.id;
          if (data.sobrietyDate && data.dischargeDate) {
            const status = String(
              data.sobrietyStatus ?? data.sobriety_status ?? ""
            )
              .toLowerCase()
              .trim();
            residentProfiles.set(residentId, {
              sobrietyDate: new Date(data.sobrietyDate),
              eventDate: new Date(data.dischargeDate),
              eventType: status === "not sober" ? "relapse" : "discharge", // Consider 'discharge' as censored for survival
              isMAT: data.isMAT === true,
              // Collect other risk factors
              childhoodTrauma: data.childhoodTrauma,
              homelessOneYear: data.homelessOneYear,
              overOneYearInPrison: data.overOneYearInPrison,
              coOccurringDiagnosis: data.coOccurringDiagnosis,
              kids: data.kids,
              strongRelationKids: data.strongRelationKids,
              therapy: data.therapy,
            });
          }
        });

        // ADD NAME MAPPING - Build name mapping like your old code
        const residentNameMap = new Map(); // name -> residentId

        // Add name mappings from residents
        residentSnapshot.docs.forEach((d) => {
          const data = d.data();
          if (data.fullName) {
            residentNameMap.set(data.fullName.toLowerCase().trim(), d.id);
          }
        });

        // Add name mappings from alumni
        alumniSnapshot.docs.forEach((d) => {
          const data = d.data();
          const residentId = data.residentId || d.id;
          if (data.fullName) {
            residentNameMap.set(data.fullName.toLowerCase().trim(), residentId);
          }
        });

        // Initialize relapseMap for buildComparison (still needed for that section)
        const relapseMap = new Map(); // uid -> Set<timeGroup>
        residentProfiles.forEach((profile, uid) => {
          if (profile.eventType === "relapse") {
            const daysUntilRelapse = Math.floor(
              (profile.eventDate - profile.sobrietyDate) / 86400000
            );
            const timeGroup = getTimeGroupForDays(daysUntilRelapse);
            if (!relapseMap.has(uid)) relapseMap.set(uid, new Set());
            relapseMap.get(uid).add(timeGroup);
          }
        });

        /* ───────────────────────────── Calculate Survival Probabilities ───────────────────────── */
        const uniqueTimePointsOrdered = Object.keys(timePointBoundaries);
        let cumulativeSurvivalProbability = 1; // Correctly initialized to 1 (for 100%)

        const calculatedSurvivalData = [];

        // Add an explicit starting point for 0 days / 0 months at 100% survival
        calculatedSurvivalData.push({
          timePoint: "0 days", // Use "0 days" or "0m" for the very start
          survivalProbability: 100.0,
          atRisk: residentProfiles.size, // All initial residents are at risk at the start
          relapsed: 0,
          censored: 0,
        });

        // Create a set of all unique resident IDs initially, representing everyone at risk at time 0
        let currentSurvivorsInPreviousPeriod = new Set(residentProfiles.keys());

        for (const timePoint of uniqueTimePointsOrdered) {
          const { start, end } = timePointBoundaries[timePoint];

          let atRiskInCurrentPeriod = 0;
          let relapsedInCurrentPeriod = 0;
          let censoredInCurrentPeriod = 0;
          let survivorsOfCurrentPeriod = new Set(); // To pass to the next iteration

          // Only iterate over residents who "survived" up to the start of this period
          for (const uid of currentSurvivorsInPreviousPeriod) {
            const profile = residentProfiles.get(uid);
            if (!profile) continue; // Should not happen with currentSurvivorsInPreviousPeriod

            const daysSoberAtEvent = Math.floor(
              (profile.eventDate - profile.sobrietyDate) / 86400000
            );

            // A person is "at risk" for this current period if their sobriety started
            // *before or at the start* of this period and their event (relapse/discharge/active)
            // is *after or at the start* of this period.
            // Since we are iterating `currentSurvivorsInPreviousPeriod`, they are inherently at risk
            // if they successfully passed the *previous* boundary.
            // We also need to make sure they actually *reached* the start of the current period.
            if (
              daysSoberAtEvent >= start ||
              profile.eventType === "active" ||
              profile.eventType === "discharge"
            ) {
              atRiskInCurrentPeriod++;

              // Check if relapse or censoring happened *within* this specific period
              const eventHappenedInThisPeriod =
                daysSoberAtEvent > start && daysSoberAtEvent <= end;

              if (
                profile.eventType === "relapse" &&
                eventHappenedInThisPeriod
              ) {
                relapsedInCurrentPeriod++;
              } else if (
                (profile.eventType === "discharge" ||
                  profile.eventType === "active") &&
                eventHappenedInThisPeriod
              ) {
                censoredInCurrentPeriod++;
              }

              // If they did not relapse *within this specific period*, they are survivors for the next period
              if (
                !(profile.eventType === "relapse" && eventHappenedInThisPeriod)
              ) {
                survivorsOfCurrentPeriod.add(uid);
              }
            } else if (
              profile.eventType === "relapse" &&
              daysSoberAtEvent <= start
            ) {
              // If a relapse occurred *before or at* the start of this period, they are not counted in `atRiskInCurrentPeriod` for this period.
              // This is implicitly handled by iterating `currentSurvivorsInPreviousPeriod`.
            }
          }

          const effectiveAtRisk =
            atRiskInCurrentPeriod - censoredInCurrentPeriod / 2;
          const periodRelapseRate =
            effectiveAtRisk > 0 ? relapsedInCurrentPeriod / effectiveAtRisk : 0;
          const periodSurvivalRate = 1 - periodRelapseRate;

          // Update cumulative probability
          cumulativeSurvivalProbability *= periodSurvivalRate;

          // Store for the original progressionData chart (relapse percentage based on who ended/currently is in this period)
          const g = timeGroups[timePoint];
          g.atRisk = atRiskInCurrentPeriod; // The at-risk population for this interval
          g.events = relapsedInCurrentPeriod; // Relapses specifically in this interval
          g.censored = censoredInCurrentPeriod; // Censored specifically in this interval
          g.survivalProb = Number(
            (cumulativeSurvivalProbability * 100).toFixed(1)
          ); // This is the final cumulative survival prob. for the chart

          calculatedSurvivalData.push({
            timePoint: timePoint,
            survivalProbability: Number(
              (cumulativeSurvivalProbability * 100).toFixed(1)
            ),
            atRisk: atRiskInCurrentPeriod,
            relapsed: relapsedInCurrentPeriod,
            censored: censoredInCurrentPeriod,
          });

          // Update the set of survivors for the next iteration
          currentSurvivorsInPreviousPeriod = survivorsOfCurrentPeriod;
        }

        setSurvivalData(calculatedSurvivalData); // Set the new state for survival data

        /* ───────────────────────────── Calculate Relapse Distribution by Phase ───────────────────────── */
        const calculateRelapseDistribution = () => {
          const distribution = {};
          let calculatedTotalRelapseCount = 0; // Use a local variable for the count

          // Initialize distribution object
          Object.keys(timePointBoundaries).forEach((timePoint) => {
            distribution[timePoint] = 0;
          });

          // Count relapses by time period
          residentProfiles.forEach((profile, uid) => {
            if (profile.eventType === "relapse") {
              calculatedTotalRelapseCount++; // Increment local variable

              const daysSoberAtRelapse = Math.floor(
                (profile.eventDate - profile.sobrietyDate) / 86400000
              );
              const relapseTimeGroup = getTimeGroupForDays(daysSoberAtRelapse);

              if (distribution[relapseTimeGroup] !== undefined) {
                distribution[relapseTimeGroup]++;
              }
            }
          });

          // Still set the state for other potential uses or if other components depend on these states
          setRelapseDistributionData(distribution);
          setTotalRelapsesCount(calculatedTotalRelapseCount);

          // Return the freshly calculated values
          return {
            newDistribution: distribution,
            newTotalCount: calculatedTotalRelapseCount,
          };
        };

        // Call the function
        const { newDistribution, newTotalCount } =
          calculateRelapseDistribution();

        /* ───────────────────────────── Process Questionnaires (for RC Scores) - WITH NAME MATCHING ─────────────────────── */
        const rawData = rcSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        let processedAssessments = 0;
        let skippedNoResident = 0;
        let nameMatchCount = 0;

        rawData.forEach((assessment) => {
          // Try to get residentId directly
          let residentId = assessment.residentId;
          let matchMethod = "direct";

          // If no match by ID, try to match by name
          if (!residentProfiles.has(residentId)) {
            const residentName = assessment.residentName || assessment.fullName;

            if (
              residentName &&
              residentNameMap.has(residentName.toLowerCase().trim())
            ) {
              // We found a name match! Use the ID from our name mapping
              residentId = residentNameMap.get(
                residentName.toLowerCase().trim()
              );
              nameMatchCount++;
              matchMethod = "name";
            }
          }

          // Now check if we have a valid resident ID by either method
          const profile = residentProfiles.get(residentId);
          if (!profile || !profile.sobrietyDate) {
            skippedNoResident++;
            return;
          }

          const ts =
            assessment.timestamp?.toDate?.() ?? new Date(assessment.timestamp);
          const daysInRecovery = Math.floor(
            (ts - profile.sobrietyDate) / 86400000
          );
          const tg = getTimeGroupForDays(daysInRecovery);

          if (timeGroups[tg]) {
            // Ensure the time group exists
            const g = timeGroups[tg];
            g.assessments.push(assessment);
            processedAssessments++;

            domains.forEach((d) => {
              if (assessment[d] !== undefined)
                g.domains[d].push(Number(assessment[d]));
            });
          }
        });

        /* ───────────────────────────── Build progression data (DISTRIBUTION-BASED RELAPSE % - Chart 1) ─────────────────────── */
        const allDomainsProgressionData = {};

        domains.forEach((domainKey) => {
          allDomainsProgressionData[domainKey] = Object.keys(timeGroups).map(
            (tp) => {
              const g = timeGroups[tp];
              const avg = g.domains[domainKey].length
                ? g.domains[domainKey].reduce((s, v) => s + v, 0) /
                  g.domains[domainKey].length
                : 0;

              // NEW WAY (using the directly returned values):
              const relapsesInThisPeriod = newDistribution[tp] || 0; // Use newDistribution
              const distributionPercentage =
                newTotalCount > 0 // Use newTotalCount
                  ? (relapsesInThisPeriod / newTotalCount) * 100 // Use newTotalCount
                  : 0;

              return {
                timePoint: tp,
                score: Number(avg.toFixed(2)),
                relapse: Number(distributionPercentage.toFixed(1)), // Ensure this uses the new calculation
                count: relapsesInThisPeriod, // Ensure this uses the new calculation
              };
            }
          );
        });

        setProgressionData(allDomainsProgressionData.recoveryCapitalTotal);
        setDomainProgressionData(allDomainsProgressionData);

        /* ───────────────────────────── Build all risk factor data (using survival logic for counts) ─────────────────────────── */
        const allRiskFactorData = {};

        const timePointOrder = Object.keys(timePointBoundaries);

        // Helper to calculate risk factor comparison with PERIOD-SPECIFIC RELAPSE RATES (like Chart 1)
        const calculateRiskFactorRelapseRates = (
          profiles,
          riskFactorId,
          yesLabel,
          noLabel,
          getFactorValue
        ) => {
          const data = [];

          for (const timePoint of timePointOrder) {
            const { start, end } = timePointBoundaries[timePoint];

            // Count people at risk and relapses for each group IN THIS SPECIFIC PERIOD
            let atRiskYes = 0;
            let atRiskNo = 0;
            let relapsedYes = 0;
            let relapsedNo = 0;

            // RC score calculation
            let rcScoresYes = [];
            let rcScoresNo = [];

            for (const [uid, profile] of profiles.entries()) {
              const factorValue = getFactorValue(profile, uid);

              if (factorValue === true || factorValue === false) {
                const isYesGroup = factorValue;

                // Check if this person was "at risk" during this time period
                const daysSoberAtEvent = Math.floor(
                  (profile.eventDate - profile.sobrietyDate) / 86400000
                );

                // Person is at risk if they made it to the start of this period
                const madeItToStartOfPeriod =
                  daysSoberAtEvent >= start ||
                  profile.eventType === "active" ||
                  profile.eventType === "discharge";

                if (madeItToStartOfPeriod) {
                  if (isYesGroup) {
                    atRiskYes++;
                  } else {
                    atRiskNo++;
                  }

                  // Check if they relapsed DURING this specific period
                  const relapsedDuringPeriod =
                    profile.eventType === "relapse" &&
                    daysSoberAtEvent >= start &&
                    daysSoberAtEvent <= end;

                  if (relapsedDuringPeriod) {
                    if (isYesGroup) {
                      relapsedYes++;
                    } else {
                      relapsedNo++;
                    }
                  }
                }

                // Collect RC scores for this time period
                const timeGroupAssessments = timeGroups[
                  timePoint
                ].assessments.filter((assessment) => {
                  // Try to match this assessment to this resident
                  let assessmentResidentId = assessment.residentId;

                  // If no direct match, try name matching
                  if (!residentProfiles.has(assessmentResidentId)) {
                    const residentName =
                      assessment.residentName || assessment.fullName;
                    if (
                      residentName &&
                      residentNameMap.has(residentName.toLowerCase().trim())
                    ) {
                      assessmentResidentId = residentNameMap.get(
                        residentName.toLowerCase().trim()
                      );
                    }
                  }

                  return assessmentResidentId === uid;
                });

                // Add RC scores from assessments in this time period
                timeGroupAssessments.forEach((assessment) => {
                  if (assessment.recoveryCapitalTotal) {
                    if (isYesGroup) {
                      rcScoresYes.push(Number(assessment.recoveryCapitalTotal));
                    } else {
                      rcScoresNo.push(Number(assessment.recoveryCapitalTotal));
                    }
                  }
                });
              }
            }

            // Calculate RELAPSE RATES (not distribution percentages)
            const relapseRateYes =
              atRiskYes > 0 ? (relapsedYes / atRiskYes) * 100 : 0;
            const relapseRateNo =
              atRiskNo > 0 ? (relapsedNo / atRiskNo) * 100 : 0;

            // Calculate average RC scores
            const avgRcScoreYes =
              rcScoresYes.length > 0
                ? rcScoresYes.reduce((sum, score) => sum + score, 0) /
                  rcScoresYes.length
                : 0;

            const avgRcScoreNo =
              rcScoresNo.length > 0
                ? rcScoresNo.reduce((sum, score) => sum + score, 0) /
                  rcScoresNo.length
                : 0;

            data.push({
              timePoint,
              // Use RELAPSE RATES instead of distribution percentages
              [yesLabel]: Number(relapseRateYes.toFixed(1)),
              [noLabel]: Number(relapseRateNo.toFixed(1)),
              withRiskFactorCount: atRiskYes,
              withoutRiskFactorCount: atRiskNo,
              // RC score data
              [`${yesLabel}_RC`]: Number(avgRcScoreYes.toFixed(1)),
              [`${noLabel}_RC`]: Number(avgRcScoreNo.toFixed(1)),
              rcScoreCountYes: rcScoresYes.length,
              rcScoreCountNo: rcScoresNo.length,
            });
          }
          return data;
        };

        // --- Initialize factor maps from profiles (as before, but using residentProfiles) ---
        const riskFactorMaps = {
          mat: new Map(), // residentId -> boolean
          trauma: new Map(), // residentId -> boolean (normalized from "Yes"/"No")
          homeless: new Map(),
          prison: new Map(),
          diagnosis: new Map(),
          kids: new Map(),
          kidsRelation: new Map(),
          therapy: new Map(),
        };

        const processYesNo = (value) => {
          const normalized = String(value || "")
            .toUpperCase()
            .trim();
          if (normalized === "YES") return true;
          if (normalized === "NO") return false;
          return null;
        };

        // First, populate from resident profiles (for most factors)
        residentProfiles.forEach((profile, residentId) => {
          riskFactorMaps.mat.set(residentId, profile.isMAT);

          const traumaResult = processYesNo(profile.childhoodTrauma);
          if (traumaResult !== null)
            riskFactorMaps.trauma.set(residentId, traumaResult);

          const homelessResult = processYesNo(profile.homelessOneYear);
          if (homelessResult !== null)
            riskFactorMaps.homeless.set(residentId, homelessResult);

          const prisonResult = processYesNo(profile.overOneYearInPrison);
          if (prisonResult !== null)
            riskFactorMaps.prison.set(residentId, prisonResult);

          const diagnosisResult = processYesNo(profile.coOccurringDiagnosis);
          if (diagnosisResult !== null)
            riskFactorMaps.diagnosis.set(residentId, diagnosisResult);

          const kidsResult = processYesNo(profile.kids);
          if (kidsResult !== null)
            riskFactorMaps.kids.set(residentId, kidsResult);
        });

        // Second, extract therapy and kidsRelation data from questionnaires
        rawData.forEach((assessment) => {
          // Get residentId (using same logic as before with name matching)
          let residentId = assessment.residentId;

          if (!residentProfiles.has(residentId)) {
            const residentName = assessment.residentName || assessment.fullName;
            if (
              residentName &&
              residentNameMap.has(residentName.toLowerCase().trim())
            ) {
              residentId = residentNameMap.get(
                residentName.toLowerCase().trim()
              );
            }
          }

          // Only process if we have a valid resident
          if (residentId && residentProfiles.has(residentId)) {
            // Extract therapy data from questionnaire
            if (
              assessment.therapy !== undefined &&
              assessment.therapy !== null &&
              assessment.therapy !== ""
            ) {
              const therapyScore = Number(assessment.therapy);
              if (!isNaN(therapyScore)) {
                riskFactorMaps.therapy.set(residentId, therapyScore >= 3);
              }
            }

            // Extract kids relationship data from questionnaire
            // First check if they have kids (from profile or questionnaire)
            const hasKidsFromProfile =
              riskFactorMaps.kids.get(residentId) === true;
            const hasKidsFromQuestionnaire =
              processYesNo(assessment.kids) === true;

            if (
              (hasKidsFromProfile || hasKidsFromQuestionnaire) &&
              assessment.strongRelationKids !== undefined &&
              assessment.strongRelationKids !== null &&
              assessment.strongRelationKids !== ""
            ) {
              const relationScore = Number(assessment.strongRelationKids);
              if (!isNaN(relationScore)) {
                riskFactorMaps.kidsRelation.set(residentId, relationScore >= 3);
              }
            }
          }
        });

        // Build data for each risk factor using PERIOD-SPECIFIC RELAPSE RATES
        riskFactorOptions.forEach((factor) => {
          allRiskFactorData[factor.id] = calculateRiskFactorRelapseRates(
            // Changed function name
            residentProfiles,
            factor.id,
            factor.yesLabel,
            factor.noLabel,
            (profile, uid) => riskFactorMaps[factor.id].get(uid)
          );
        });

        // Store all risk factor data in state
        setRiskFactorData(allRiskFactorData);

        /* ───────────────────────────── Individual trajectories ───────────────────────── */
        const residentAssessments = {};
        const monthlyGrowthRates = {
          totalRC: [],
          domains: {
            spiritual: [],
            social: [],
            individual: [],
            physical: [],
            general: [],
          },
        };

        // Track assessments by resident - UPDATED TO USE NAME MATCHING
        rawData.forEach((assessment) => {
          // Try to get residentId directly
          let residentId = assessment.residentId;

          // If no match by ID, try to match by name
          if (!residentProfiles.has(residentId)) {
            const residentName = assessment.residentName || assessment.fullName;
            if (
              residentName &&
              residentNameMap.has(residentName.toLowerCase().trim())
            ) {
              residentId = residentNameMap.get(
                residentName.toLowerCase().trim()
              );
            }
          }

          if (!residentId) return;

          if (!residentAssessments[residentId]) {
            residentAssessments[residentId] = [];
          }

          const profile = residentProfiles.get(residentId);
          if (!profile || !profile.sobrietyDate) return;

          const timestamp =
            assessment.timestamp?.toDate?.() ?? new Date(assessment.timestamp);
          const daysInRecovery = Math.floor(
            (timestamp - profile.sobrietyDate) / 86400000
          );

          residentAssessments[residentId].push({
            timestamp,
            daysInRecovery,
            rcScore: Number(assessment.recoveryCapitalTotal || 0),
            domains: {
              spiritual: Number(assessment.spiritualCapitalScore || 0),
              social: Number(assessment.socialCapitalScore || 0),
              individual: Number(assessment.individualCapitalScore || 0),
              physical: Number(assessment.physicalCapitalScore || 0),
              general: Number(assessment.generalCapitalScore || 0),
            },
          });
        });

        // Process growth rates and trajectories
        const trajectories = [];

        Object.keys(residentAssessments).forEach((residentId) => {
          const assessments = residentAssessments[residentId];

          if (assessments.length >= 2) {
            assessments.sort((a, b) => a.daysInRecovery - b.daysInRecovery);

            const profile = residentProfiles.get(residentId);
            const everRelapsed = profile
              ? profile.eventType === "relapse"
              : false;

            const firstAssessment = assessments[0];
            const lastAssessment = assessments[assessments.length - 1];
            const monthsDiff =
              (lastAssessment.daysInRecovery - firstAssessment.daysInRecovery) /
              30;

            if (monthsDiff > 0) {
              const rcGrowthRate =
                (lastAssessment.rcScore - firstAssessment.rcScore) / monthsDiff;
              monthlyGrowthRates.totalRC.push(rcGrowthRate);

              const domainGrowth = {
                spiritual:
                  (lastAssessment.domains.spiritual -
                    firstAssessment.domains.spiritual) /
                  monthsDiff,
                social:
                  (lastAssessment.domains.social -
                    firstAssessment.domains.social) /
                  monthsDiff,
                individual:
                  (lastAssessment.domains.individual -
                    firstAssessment.domains.individual) /
                  monthsDiff,
                physical:
                  (lastAssessment.domains.physical -
                    firstAssessment.domains.physical) /
                  monthsDiff,
                general:
                  (lastAssessment.domains.general -
                    firstAssessment.domains.general) /
                  monthsDiff,
              };

              monthlyGrowthRates.domains.spiritual.push(domainGrowth.spiritual);
              monthlyGrowthRates.domains.social.push(domainGrowth.social);
              monthlyGrowthRates.domains.individual.push(
                domainGrowth.individual
              );
              monthlyGrowthRates.domains.physical.push(domainGrowth.physical);
              monthlyGrowthRates.domains.general.push(domainGrowth.general);
            }

            const trajectory = assessments
              .filter((assessment) => assessment.daysInRecovery >= 0)
              .map((assessment) => ({
                residentId,
                daysInRecovery: assessment.daysInRecovery,
                rcScore: assessment.rcScore,
                relapsed: everRelapsed,
              }));

            if (
              trajectory.length > 0 &&
              trajectory.every(
                (point) => point.rcScore >= 120 && point.rcScore <= 185
              )
            ) {
              trajectory.didRelapse = everRelapsed;
              trajectories.push(trajectory);
            }
          }
        });

        setProgressionData(allDomainsProgressionData.recoveryCapitalTotal);
        setSurvivalData(calculatedSurvivalData);

        const calcAverage = (arr) =>
          arr.length ? arr.reduce((sum, val) => sum + val, 0) / arr.length : 0;

        const avgGrowthRates = {
          totalRC: calcAverage(monthlyGrowthRates.totalRC),
          domains: {
            spiritual: calcAverage(monthlyGrowthRates.domains.spiritual),
            social: calcAverage(monthlyGrowthRates.domains.social),
            individual: calcAverage(monthlyGrowthRates.domains.individual),
            physical: calcAverage(monthlyGrowthRates.domains.physical),
            general: calcAverage(monthlyGrowthRates.domains.general),
          },
        };

        setIndividualTrajectories(trajectories);
        setGrowthRates(avgGrowthRates);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching recovery-capital progression data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchProgressionData();
  }, [organizationId]);

  useEffect(() => {
    if (Object.keys(domainProgressionData).length > 0) {
      const descriptions = {};

      // Generate descriptions for each domain
      domainOptions.forEach((domain) => {
        if (
          domain.id !== "recoveryCapitalTotal" &&
          domainProgressionData[domain.id]
        ) {
          descriptions[domain.id] = generateDomainDescription(
            domain.id,
            domainProgressionData[domain.id]
          );
        }
      });

      setDomainDescriptions(descriptions);
    }
  }, [domainProgressionData]);

  // Handle risk factor change
  const handleRiskFactorChange = (factorId) => {
    setSelectedRiskFactor(factorId);
    setIsRiskFactorDropdownOpen(false);
  };

  const handleMetricChange = (metricId) => {
    setSelectedMetric(metricId);
    setIsMetricDropdownOpen(false);
  };

  // Handle domain change
  const handleDomainChange = (domainId) => {
    setSelectedDomain(domainId);
    setIsDropdownOpen(false);
    // Update the current progression data with the selected domain's data
    if (domainProgressionData[domainId]) {
      setProgressionData(domainProgressionData[domainId]);
    }
  };

  // Determine the Y-axis domain for scores based on selected domain
  const getScoreDomain = () => {
    if (selectedDomain === "recoveryCapitalTotal") {
      return [140, 165]; // Larger range for total score
    } else {
      return [20, 40]; // Smaller range for individual domains
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  // Get the current domain configuration
  const currentDomain = getSelectedDomainConfig();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Recovery Capital Progression
        </h2>
      </div>

      {/* RC Progression Chart with Domain Filter */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-blue-400">
              Recovery Capital Over Time
            </h3>
            <span className="relative inline-block group">
              <Info size={16} className="text-slate-400 cursor-pointer" />
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                <div className="space-y-2">
                  <div>
                    <strong className="text-blue-400">What this shows:</strong>{" "}
                    Recovery Capital scores (blue line) and relapse distribution
                    percentages (red line) across different time periods in
                    recovery.
                  </div>
                  <div>
                    <strong className="text-amber-400">
                      Relapse % Calculation:
                    </strong>{" "}
                    This chart shows what percentage of ALL relapses occurred
                    during each time period. For example, if 36% of all relapses
                    happened in the 0-3 month window, that period shows 36%.
                  </div>
                  <div>
                    <strong className="text-red-400">Key Difference:</strong>{" "}
                    Unlike other charts that show "relapse rates" (what % of
                    people in each period relapsed), this shows the
                    "distribution" of when relapses occur across the recovery
                    timeline.
                  </div>
                  <div>
                    <strong className="text-green-400">
                      Questions it answers:
                    </strong>{" "}
                    "When do most relapses happen?" and "How does Recovery
                    Capital develop over time?"
                  </div>
                  <div>
                    <strong className="text-purple-400">Data source:</strong> RC
                    scores from questionnaires + relapse timing from resident
                    profiles.
                  </div>
                </div>
              </span>
            </span>
          </div>

          {/* Domain Selection Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
            >
              {currentDomain.icon && (
                <currentDomain.icon
                  size={16}
                  className={`text-${currentDomain.color}`}
                />
              )}
              <span>{currentDomain.name}</span>
              <ChevronDown size={16} />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                <ul className="py-1">
                  {domainOptions.map((domain) => (
                    <li key={domain.id}>
                      <button
                        onClick={() => handleDomainChange(domain.id)}
                        className={`flex items-center gap-2 w-full text-left px-4 py-2 text-sm ${
                          selectedDomain === domain.id
                            ? "bg-slate-700 text-white"
                            : "text-slate-300 hover:bg-slate-700 hover:text-white"
                        }`}
                      >
                        {domain.icon && <domain.icon size={16} />}
                        {domain.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={progressionData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
              />
              <XAxis
                dataKey="timePoint"
                stroke="#94a3b8"
                tickFormatter={formatTimePoint}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke={currentDomain.color}
                domain={getScoreDomain()}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#f87171"
                domain={[0, 40]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "0.5rem",
                }}
                itemStyle={{ color: "#e2e8f0" }}
                labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                formatter={(value, name) => {
                  if (name === "Relapse %") {
                    return [`${value}% of all relapses`, name];
                  }
                  return [
                    value,
                    selectedDomain === "recoveryCapitalTotal"
                      ? "RC Score"
                      : currentDomain.name,
                  ];
                }}
              />
              <Legend
                formatter={(value) => {
                  if (value === "score") {
                    return selectedDomain === "recoveryCapitalTotal"
                      ? "RC Score"
                      : currentDomain.name;
                  }
                  return value;
                }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="score"
                name="score"
                stroke={currentDomain.color}
                activeDot={{ r: 8 }}
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="relapse"
                name="Relapse %"
                stroke="#f87171"
                activeDot={{ r: 6 }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">
              Key Observations as of 6/1/25 (Data is ever changing)
            </h4>
            <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
              {selectedDomain === "recoveryCapitalTotal" ? (
                <>
                  <li>
                    RC scores show steady increase over time, with significant
                    gains after 9 Months
                  </li>
                  <li>
                    Plateau effect observed after approximately 18&nbsp;months
                    (540 days) with a slight reduction back down to a most
                    likely baseline (Pink cloud effect?)
                  </li>
                  <li>
                    A score of 150 seems to be a significant buffer threshold to
                    protect against relapse. The cross-over point at the
                    6-9&nbsp;month mark with the 150 score could indicate a
                    critical treatment time for individuals, suggesting against
                    the typical 28-day treatment model (even 60-90&nbsp;days).
                  </li>
                </>
              ) : selectedDomain === "spiritualCapitalScore" ? (
                <>
                  <li>Highest domain score at intake among all domains.</li>
                  <li>Shows plateau around 12-18&nbsp;months.</li>
                  <li>
                    Correlates strongly with individual and social domains.
                  </li>
                </>
              ) : selectedDomain === "socialCapitalScore" ? (
                <>
                  <li>Highest average score among all domains.</li>
                  <li>Very low variance and very few outliers.</li>
                  <li>Highly correlated to overall RC score.</li>
                  <li>
                    Shows strong correlation with Individual Capital (which has
                    the strongest correlation to RC score).
                  </li>
                </>
              ) : selectedDomain === "individualCapitalScore" ? (
                <>
                  <li>
                    Acts as a “hub domain” with strong connections to other
                    dimensions.
                  </li>
                  <li>Shows steady improvement over time.</li>
                  <li>
                    Virtually no severe lows, suggesting that the 1-4 Likert
                    scale may lack sensitivity; a 1-7 scale could capture more
                    nuance.
                  </li>
                </>
              ) : selectedDomain === "generalCapitalScore" ? (
                <>
                  <li>
                    Steepest early climb: ~6-point (
                    <span className="whitespace-nowrap">≈25 %</span>) increase
                    within the first 12&nbsp;months.
                  </li>
                  <li>Greatest spread: highest variance among all domains.</li>
                  <li>
                    High scores likely indicate daily structure via employment,
                    sponsorship, therapy, and reliable transport
                  </li>
                </>
              ) : selectedDomain === "physicalCapitalScore" ? (
                <>
                  <li>
                    Slow mover: Sub-point improvement over 12&nbsp;months is the
                    smallest among domains, reflecting the difficulty of
                    changing pain, sleep, diet, and fitness.
                  </li>
                  <li>
                    Highest prevalence of high outliers: A niche subgroup excels
                    physically; their routines may offer insights.
                  </li>
                  <li>
                    Strong link to Individual Capital: Correlation suggests
                    self-regulation and mind/body wellness evolve together.
                  </li>
                </>
              ) : (
                <></>
              )}
            </ul>
          </div>
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">Relapse Dynamics</h4>
            <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
              <li>
                Relapse risk starts highest in early recovery (35%) and shows a
                steady, consistent decline over time as recovery capital
                strengthens.
              </li>
              <li>
                Significant protection develops gradually, with relapse rates
                dropping to approximately 9% by the 9-12 month mark and
                continuing to decrease.
              </li>
              <li>
                After 24 months in recovery, relapse risk approaches zero as RC
                scores reach their peak strength.
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Critical Threshold Theory Section - ALWAYS VISIBLE */}
      <div className="mt-4 bg-blue-900/30 p-5 rounded-xl border border-blue-700/50 backdrop-blur-sm">
        <h4 className="text-xl font-bold text-blue-300 mb-6 flex items-center">
          <AlertTriangle size={18} className="text-yellow-500 mr-2" />
          Critical Threshold
        </h4>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-slate-800/60 rounded-lg p-4 relative">
            <div className="absolute -top-3 -left-3 bg-yellow-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">
              1
            </div>
            <h5 className="text-white font-medium mb-2 pl-5">
              Protection Threshold
            </h5>
            <p className="text-slate-300 text-sm">
              The data reveals a critical threshold around{" "}
              <span className="text-yellow-400 font-semibold">
                150 RC points
              </span>
              , which is reached after the 9-month mark. This appears to be when
              recovery capital becomes sufficient to buffer against relapse
              risk, following a high-risk period at 0-9 months.
            </p>
            <div className="flex justify-center mt-3">
              <div className="w-40 h-1 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"></div>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">High Risk</span>
              <span className="text-xs text-slate-400">Protected</span>
            </div>
          </div>

          <div className="bg-slate-800/60 rounded-lg p-4 relative">
            <div className="absolute -top-3 -left-3 bg-yellow-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">
              2
            </div>
            <h5 className="text-white font-medium mb-2 pl-5">
              Recovery Phases
            </h5>
            <div className="space-y-1 text-sm">
              <div
                onClick={() =>
                  document
                    .getElementById("highest-risk-phase")
                    .classList.toggle("hidden")
                }
                className="flex items-center cursor-pointer"
              >
                <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                <p className="text-white font-medium">
                  Highest Risk Period (0-90 days)
                </p>
              </div>
              <p
                id="highest-risk-phase"
                className="pl-5 text-slate-300 text-xs"
              >
                Peak vulnerability with ~35% period relapse rate - Early
                recovery phase with moderate RC scores (~147)
              </p>

              <div
                onClick={() =>
                  document
                    .getElementById("declining-risk-phase")
                    .classList.toggle("hidden")
                }
                className="flex items-center cursor-pointer"
              >
                <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                <p className="text-white font-medium">
                  Declining Risk Period (91-180 days)
                </p>
              </div>
              <p
                id="declining-risk-phase"
                className="pl-5 text-slate-300 text-xs"
              >
                Risk dropping to ~25% as RC scores improve - Building stability
                but still vulnerable
              </p>

              <div
                onClick={() =>
                  document
                    .getElementById("threshold-crossing-phase")
                    .classList.toggle("hidden")
                }
                className="flex items-center cursor-pointer"
              >
                <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                <p className="text-white font-medium">
                  Threshold Crossing (181-270 days)
                </p>
              </div>
              <p
                id="threshold-crossing-phase"
                className="pl-5 text-slate-300 text-xs"
              >
                Risk continues declining to ~18% as RC scores cross the
                protective 150 threshold - Critical transition point
              </p>

              <div
                onClick={() =>
                  document
                    .getElementById("protected-phase")
                    .classList.toggle("hidden")
                }
                className="flex items-center cursor-pointer"
              >
                <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                <p className="text-white font-medium">
                  Protected Phase (271-540 days)
                </p>
              </div>
              <p id="protected-phase" className="pl-5 text-slate-300 text-xs">
                Dramatic risk reduction to ~9% - RC scores above threshold
                provide increasing protection
              </p>

              <div
                onClick={() =>
                  document
                    .getElementById("recovery-stability-phase")
                    .classList.toggle("hidden")
                }
                className="flex items-center cursor-pointer"
              >
                <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                <p className="text-white font-medium">
                  Recovery Stability (18+ months)
                </p>
              </div>
              <p
                id="recovery-stability-phase"
                className="pl-5 text-slate-300 text-xs"
              >
                Near 0% relapse rate - Maximum RC protection achieved,
                essentially relapse-proof phase
              </p>
            </div>
          </div>

          <div className="bg-slate-800/60 rounded-lg p-4 relative">
            <div className="absolute -top-3 -left-3 bg-yellow-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">
              3
            </div>
            <h5 className="text-white font-medium mb-2 pl-5">
              Clinical Implications
            </h5>
            <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
              <li>
                <span className="text-white">Critical Window:</span> Maximum
                intensive support needed in first 90 days when 36.4% of all
                relapses occur - the highest risk period
              </li>
              <li>
                <span className="text-white">Early Focus:</span> First 6 months
                account for 63.7% of all relapses (36.4% + 27.3%), requiring
                sustained high-level intervention
              </li>
              <li>
                <span className="text-white">Treatment Duration:</span> Programs
                should extend to 9+ months to help residents cross the
                protective RC threshold of 150
              </li>
              <li>
                <span className="text-white">Resource Allocation:</span>{" "}
                Front-load maximum resources in first 90 days, then maintain
                elevated support through month 6
              </li>
              <li>
                <span className="text-white">Assessment Target:</span> RC score
                of 150+ seems to be a meaningful benchmark - once crossed around
                6-9 months, relapse risk drops dramatically
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Survival Probability Chart (NEW) */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-green-400">
            Cumulative Relapse-Free Probability
          </h3>
          <span className="relative inline-block group">
            <Info size={16} className="text-slate-400 cursor-pointer" />
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-green-400">What this shows:</strong>{" "}
                  This chart tracks how many people stay sober over time. It
                  shows the percentage of people who haven't relapsed yet at
                  each time point.
                </div>
                <div>
                  <strong className="text-blue-400">
                    Questions it answers:
                  </strong>{" "}
                  "What are my chances of staying sober for 6 months?" or "When
                  do most people struggle the most?"
                </div>
                <div>
                  <strong className="text-amber-400">How it works:</strong> The
                  line starts at 100% (everyone sober) and goes down as people
                  experience setbacks. The steeper the drop, the more
                  challenging that time period is.
                </div>
              </div>
            </span>
          </span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={survivalData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
              />
              <XAxis
                dataKey="timePoint"
                stroke="#94a3b8"
                tickFormatter={formatTimePoint}
              />
              <YAxis
                orientation="left"
                stroke="#10B981" // Green for survival
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "0.5rem",
                }}
                itemStyle={{ color: "#e2e8f0" }}
                labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                formatter={(value, name, props) => {
                  if (name === "Relapse-Free") {
                    return [
                      `${value}% (${props.payload.atRisk} people tracked)`,
                      name,
                    ];
                  }
                  return [value, name];
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="survivalProbability"
                name="Relapse-Free"
                stroke="#10B981" // Green
                activeDot={{ r: 8 }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 bg-slate-900/50 p-4 rounded-lg">
          <h4 className="text-white font-semibold mb-2">
            How to Read This Chart
          </h4>
          <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
            <li>
              The green line shows{" "}
              <span className="font-semibold text-white">
                what percentage of people are still sober
              </span>{" "}
              at each point in time. It starts at 100% (everyone begins sober)
              and drops when people experience setbacks.
            </li>
            <li>
              <span className="font-semibold text-white">Steep drops</span>{" "}
              indicate challenging periods where more people struggled, while{" "}
              <span className="font-semibold text-white">flatter sections</span>{" "}
              show more stable times.
            </li>
          </ul>
        </div>
      </div>

      {/* Risk/Protective Factor Longitudinal Chart */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 mt-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-blue-400">
              Risk/Protective Factor Longitudinal Analysis
            </h3>
            <span className="relative inline-block group">
              <Info size={16} className="text-slate-400 cursor-pointer" />
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                <div className="space-y-2">
                  <div>
                    <strong className="text-purple-400">Data source:</strong>{" "}
                    ALL residents (current + alumni) who reached each time
                    period, unlike Overview tab (alumni outcomes only).
                  </div>
                  <div>
                    <strong className="text-blue-400">Shows:</strong>{" "}
                    Period-specific relapse rates by risk factor during each
                    recovery phase (0-3m, 3-6m, etc.).
                  </div>
                  <div>
                    <strong className="text-green-400">
                      Example questions:
                    </strong>{" "}
                    "What's my relapse risk in months 3-6 if I have prison
                    history?" or "Which factors create highest vulnerability in
                    early recovery?"
                  </div>
                  <div>
                    <strong className="text-amber-400">Calculation:</strong> For
                    each period, count people "at risk" with the factor, then
                    calculate what % relapsed during that window.
                  </div>
                  <div>
                    <strong className="text-red-400">Key difference:</strong>{" "}
                    Shows when relapses happen vs. Overview's who eventually
                    relapses. Higher sample sizes include current residents.
                  </div>
                </div>
              </span>
            </span>
          </div>

          <div className="flex gap-2">
            {/* Metric Selection Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsMetricDropdownOpen(!isMetricDropdownOpen)}
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
              >
                <span>
                  {metricOptions.find((opt) => opt.id === selectedMetric)
                    ?.name || "Select Metric"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isMetricDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                  <ul className="py-1">
                    {metricOptions.map((metric) => (
                      <li key={metric.id}>
                        <button
                          onClick={() => handleMetricChange(metric.id)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            selectedMetric === metric.id
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          {metric.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Risk Factor Selection Dropdown */}
            <div className="relative">
              <button
                onClick={() =>
                  setIsRiskFactorDropdownOpen(!isRiskFactorDropdownOpen)
                }
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
              >
                <span>
                  {riskFactorOptions.find(
                    (opt) => opt.id === selectedRiskFactor
                  )?.name || "Select Factor"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isRiskFactorDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                  <ul className="py-1">
                    {riskFactorOptions.map((factor) => (
                      <li key={factor.id}>
                        <button
                          onClick={() => handleRiskFactorChange(factor.id)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            selectedRiskFactor === factor.id
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          {factor.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={riskFactorData[selectedRiskFactor] || []}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
              />
              <XAxis
                dataKey="timePoint"
                stroke="#94a3b8"
                tickFormatter={formatTimePoint}
              />
              <YAxis
                orientation="left"
                stroke={
                  metricOptions.find((m) => m.id === selectedMetric)?.color ||
                  "#3B82F6"
                }
                domain={selectedMetric === "relapse" ? [0, 50] : [140, 165]}
                tickFormatter={(value) =>
                  selectedMetric === "relapse" ? `${value}%` : value
                }
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "0.5rem",
                }}
                itemStyle={{ color: "#e2e8f0" }}
                labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                formatter={(value, name, props) => {
                  const factor = riskFactorOptions.find(
                    (opt) => opt.id === selectedRiskFactor
                  );

                  if (selectedMetric === "relapse") {
                    const isYesGroup = name === factor.yesLabel;
                    const atRiskCount = isYesGroup
                      ? props.payload.withRiskFactorCount
                      : props.payload.withoutRiskFactorCount;

                    const relapsedCount = Math.round(
                      (value / 100) * atRiskCount
                    );

                    return [
                      `${value.toFixed(1)}% relapse rate`,
                      `${name} (${relapsedCount}/${atRiskCount} relapsed)`,
                    ];
                  } else {
                    const count = name.includes(factor.yesLabel)
                      ? props.payload.rcScoreCountYes
                      : props.payload.rcScoreCountNo;
                    return [
                      `${value.toFixed(1)} (n=${count})`,
                      name.replace("_RC", ""),
                    ];
                  }
                }}
              />
              <Legend
                formatter={(value) => {
                  if (selectedMetric === "rcScore") {
                    return value.replace("_RC", "");
                  }
                  return value;
                }}
              />
              {(() => {
                const factor = riskFactorOptions.find(
                  (opt) => opt.id === selectedRiskFactor
                );
                const dataKeyYes =
                  selectedMetric === "relapse"
                    ? factor.yesLabel
                    : `${factor.yesLabel}_RC`;
                const dataKeyNo =
                  selectedMetric === "relapse"
                    ? factor.noLabel
                    : `${factor.noLabel}_RC`;

                return [
                  <Line
                    key="with"
                    type="monotone"
                    dataKey={dataKeyYes}
                    name={dataKeyYes}
                    stroke="#3B82F6"
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />,
                  <Line
                    key="without"
                    type="monotone"
                    dataKey={dataKeyNo}
                    name={dataKeyNo}
                    stroke="#EC4899"
                    activeDot={{ r: 6 }}
                    strokeWidth={2}
                  />,
                ];
              })()}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Update the Key Insights section to be dynamic based on selected metric */}
        <div className="mt-4 bg-slate-900/50 p-4 rounded-lg">
          <h4 className="text-white font-semibold mb-2">
            Key Insights -{" "}
            {selectedMetric === "relapse"
              ? "Relapse Patterns"
              : "Recovery Capital Patterns"}
          </h4>
          <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
            {selectedMetric === "relapse" ? (
              // Complete relapse insights for all risk factors
              selectedRiskFactor === "mat" ? (
                <>
                  <li>
                    MAT users consistently show higher relapse rates throughout
                    the first 9 months of recovery
                  </li>
                  <li>
                    Both groups achieve similar long-term success rates after
                    18+ months
                  </li>
                </>
              ) : selectedRiskFactor === "trauma" ? (
                <>
                  <li>
                    Individuals with childhood trauma history show different
                    recovery trajectories with higher vulnerability during early
                    recovery
                  </li>
                  <li>
                    Simply not enough data. There are zero reoccurrences so far
                    for those who suggest they didn't experience childhood
                    trauma. This was not something we tracked early on, so many
                    of these risk factors have less data then the rest.
                  </li>
                </>
              ) : selectedRiskFactor === "homeless" ? (
                <>
                  <li>
                    Those who have been homeless for over a year of their lives
                    face additional challenges in early recovery, with more than
                    double the relapse rates in the first 6 months.
                  </li>
                  <li>
                    Housing stability appears to be a critical protective
                    factor, particularly during the vulnerable 6-9 month period
                  </li>
                </>
              ) : selectedRiskFactor === "prison" ? (
                <>
                  <li>
                    People with prison history show consistently higher relapse
                    rates throughout early recovery, with peak vulnerability
                    around 3-6 months (12% vs 6% for those without prison
                    history)
                  </li>
                  <li>
                    Both groups achieve similar low relapse rates after 9
                    months, suggesting prison history creates early recovery
                    challenges that can be overcome with sustained support
                  </li>
                  <li>
                    The 2-3x higher relapse risk for those with criminal justice
                    involvement indicates need for specialized early
                    intervention and support programs
                  </li>
                </>
              ) : selectedRiskFactor === "diagnosis" ? (
                <>
                  <li>
                    Co-occurring mental health diagnoses significantly impact
                    vulnerability to relapse, particularly in first 9 months.
                  </li>
                  <li>
                    In the very few people who suggested against a co-occurring
                    diagnosis, all of them remained sober throughout their time
                    at the Reverence House.
                  </li>
                </>
              ) : selectedRiskFactor === "kids" ? (
                <>
                  <li>
                    Parental responsibilities can function as both motivation
                    for recovery and a source of stress, with different impacts
                    across recovery phases
                  </li>
                  <li>
                    Support for parents in recovery may be most critical during
                    the 6-9 month vulnerability window
                  </li>
                </>
              ) : selectedRiskFactor === "kidsRelation" ? (
                <>
                  <li>
                    Quality of relationship with children appears to
                    significantly impact recovery outcomes, suggesting
                    family-focused interventions may be beneficial
                  </li>
                  <li>
                    Strong parent-child relationships correlate with increased
                    recovery capital and lower relapse rates in later recovery
                    phases
                  </li>
                  <li>
                    The dramatic difference between strong and weak
                    relationships shows that relationship quality with children
                    may be the key protective factor
                  </li>
                </>
              ) : selectedRiskFactor === "therapy" ? (
                <>
                  <li>
                    Therapy participation shows protective effects that become
                    more pronounced over time
                  </li>
                  <li>
                    Those engaged in therapy demonstrate more stable recovery
                    capital development and lower relapse vulnerability
                  </li>
                </>
              ) : (
                <>
                  <li>
                    Relapse pattern analysis for{" "}
                    {
                      riskFactorOptions.find(
                        (opt) => opt.id === selectedRiskFactor
                      )?.name
                    }
                  </li>
                </>
              )
            ) : (
              // New RC score insights
              <>
                <li>
                  Recovery Capital scores show how different risk factors impact
                  the development of protective resources over time
                </li>
                <li>
                  Groups with higher RC scores generally demonstrate lower
                  relapse vulnerability, particularly after crossing the
                  150-point threshold
                </li>
                <li>
                  The gap between risk factor groups in RC development may
                  indicate where targeted interventions could be most effective
                </li>
              </>
            )}
            <li>
              <span className="text-amber-400 font-medium">
                Note on sample size:
              </span>{" "}
              These patterns should be interpreted cautiously as some charts
              have small sample sizes, which can cause fluctuations.
            </li>
          </ul>
        </div>
      </div>

      {/* Individual Growth Section */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-blue-400">
            Individual Growth Analysis
          </h3>
          <button
            onClick={() => setShowTrajectories(!showTrajectories)}
            className="bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
          >
            {showTrajectories
              ? "Hide Trajectories"
              : "Show Individual Trajectories"}
          </button>
        </div>

        {showTrajectories && (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.1)"
                />
                <XAxis
                  type="number"
                  dataKey="daysInRecovery"
                  domain={[0, 730]}
                  tickFormatter={(value) => {
                    const months = Math.floor(value / 30);
                    return months > 0 ? `${months}m` : value;
                  }}
                  label={{
                    value: "Time in Recovery (months)",
                    position: "insideBottomRight",
                    offset: -5,
                    fill: "#94a3b8",
                  }}
                  stroke="#94a3b8"
                />
                <YAxis
                  orientation="left"
                  domain={[120, 185]}
                  allowDataOverflow={true} // This is important!
                  label={{
                    value: "RC Score",
                    angle: -90,
                    position: "insideLeft",
                    fill: "#94a3b8",
                  }}
                  stroke="#94a3b8"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: "0.5rem",
                  }}
                  itemStyle={{ color: "#e2e8f0" }}
                  labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  formatter={(value, name, props) => [
                    value,
                    name === "rcScore" ? "RC Score" : name,
                  ]}
                  labelFormatter={(value) =>
                    `Recovery time: ${formatDaysToMonthsAndDays(value)}`
                  }
                />

                {/* Individual trajectories */}
                {individualTrajectories.map((trajectory, idx) => (
                  <Line
                    key={idx}
                    data={trajectory}
                    type="monotone"
                    dataKey="rcScore"
                    // Use trajectory.didRelapse instead of just the first point
                    stroke={trajectory.didRelapse ? "#EF4444" : "#10B981"}
                    strokeOpacity={0.4}
                    strokeWidth={1}
                    dot={false}
                    activeDot={{ r: 4 }}
                    name={`Resident ${idx + 1} ${
                      trajectory.didRelapse ? "(Relapsed)" : ""
                    }`}
                  />
                ))}

                {/* Mean trajectory overlay */}
                <Line
                  data={progressionData.map((p) => ({
                    daysInRecovery:
                      p.timePoint === "0-90 days"
                        ? 45
                        : p.timePoint === "91-180 days"
                        ? 135
                        : p.timePoint === "181-270 days"
                        ? 225
                        : p.timePoint === "271-365 days"
                        ? 318
                        : p.timePoint === "366-540 days"
                        ? 453
                        : p.timePoint === "541-730 days"
                        ? 635
                        : 760,
                    rcScore: p.score,
                  }))}
                  type="monotone"
                  dataKey="rcScore"
                  stroke="#3B82F6"
                  strokeWidth={3}
                  dot={{ r: 6 }}
                  name="Average RC Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="mt-3 bg-blue-900/30 p-3 rounded-lg border border-blue-700/50">
          <p className="text-sm text-slate-300">
            <span className="font-medium text-blue-300">
              Note on data selection:
            </span>{" "}
            This visualization only includes residents who completed 2+ recovery
            capital questionnaires, with the second assessment occurring at
            approximately the 3-month mark. This naturally selects for residents
            who maintained recovery long enough to remain in the house for at
            least 3 months, which explains the notably higher success rates seen
            in these trajectories compared to overall house statistics.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">
              Monthly Growth Rates
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">Total RC</span>
                <span
                  className={`font-bold ${
                    growthRates.totalRC > 0
                      ? "text-emerald-400"
                      : "text-red-400"
                  }`}
                >
                  {growthRates.totalRC > 0 ? "+" : ""}
                  {growthRates.totalRC.toFixed(1)} pts/month
                </span>
              </div>
              {Object.entries(growthRates.domains).map(([domain, rate]) => (
                <div key={domain} className="flex justify-between items-center">
                  <span className="text-slate-300">
                    {domain.charAt(0).toUpperCase() + domain.slice(1)}
                  </span>
                  <span
                    className={`font-bold ${
                      rate > 0 ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {rate > 0 ? "+" : ""}
                    {rate.toFixed(1)} pts/month
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">Growth Patterns</h4>
            <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
              <li>
                Fastest growing domain:{" "}
                <span className="text-emerald-400 font-semibold">
                  {Object.entries(growthRates.domains)
                    .sort((a, b) => b[1] - a[1])[0][0]
                    .charAt(0)
                    .toUpperCase() +
                    Object.entries(growthRates.domains)
                      .sort((a, b) => b[1] - a[1])[0][0]
                      .slice(1)}
                </span>{" "}
                (+0.9 pts/month) - tends to improve as residents develop more
                structure and stability
              </li>
              <li>
                Slowest growing domain:{" "}
                <span className="text-amber-400 font-semibold">
                  {Object.entries(growthRates.domains)
                    .sort((a, b) => a[1] - b[1])[0][0]
                    .charAt(0)
                    .toUpperCase() +
                    Object.entries(growthRates.domains)
                      .sort((a, b) => a[1] - b[1])[0][0]
                      .slice(1)}
                </span>{" "}
                (+0.0 pts/month) - starts high but plateaus early, suggesting
                it's already a strength for many at entry
              </li>
              <li>
                Initial RC gains of {growthRates.totalRC.toFixed(1)} points per
                month can lead to ≈{(growthRates.totalRC * 6).toFixed(1)} point
                improvement in the critical first 6 months
              </li>
              <li>
                Growth rates suggest{" "}
                {Math.ceil((150 - 140) / growthRates.totalRC)} months to reach
                the protective threshold of 150 RC points from a typical
                starting score
              </li>
              <li>
                Domains grow in a predictable sequence: spiritual/social first,
                followed by individual, with physical/general improving later in
                recovery
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Domain Progression and Life Events */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-xl font-bold text-blue-400 mb-4">
            Domain Development Patterns
          </h3>
          <div className="space-y-4">
            {domainOptions.slice(1).map((domain) => (
              <div
                className={`bg-slate-900/50 p-4 rounded-lg ${
                  selectedDomain === domain.id ? "ring-2 ring-blue-500" : ""
                }`}
                key={domain.id}
                onClick={() => handleDomainChange(domain.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="flex items-center gap-3 mb-2">
                  {domain.icon && (
                    <domain.icon
                      className={`text-${domain.color.replace("#", "")}`}
                      size={18}
                    />
                  )}
                  <h4 className="text-white font-semibold">{domain.name}</h4>
                </div>

                {/* Show domain progression in text */}
                <p className="text-slate-300 text-sm">
                  {domainDescriptions[domain.id] || "Loading domain data..."}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-xl font-bold text-blue-400 mb-4">
            Life Events &amp; RC Dynamics
          </h3>

          <div className="space-y-4">
            {/* ─────────────────── Critical Events Impact ─────────────────── */}
            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Critical Events Impact
              </h4>
              <p className="text-slate-300 text-sm">
                Large single-survey swings in Recovery Capital often follow
                acute stressors or major breakthroughs. The table below captures
                every change of&nbsp;≥ ±10&nbsp;points in the current dataset
                (181 questionnaires).
              </p>
              <ul className="mt-3 space-y-2 text-slate-300 text-sm list-disc pl-5">
                <li>
                  Largest gain:&nbsp;
                  <span className="font-semibold">+32 pts</span> (9-month
                  check-in)
                </li>
                <li>
                  Largest drop:&nbsp;
                  <span className="font-semibold">−29 pts</span> (3-month
                  check-in)
                </li>
                <li>
                  Significant swings:&nbsp;24 upward &amp; 9 downward surveys
                  (average&nbsp;±15 pts)
                </li>
              </ul>
            </div>
            {/* ─────────────────── Domain Vulnerability ─────────────────── */}
            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                Domain Vulnerability
              </h4>
              <p className="text-slate-300 text-sm">
                Domains differ in how strongly they react to acute stressors:
              </p>

              <div className="mt-3 grid grid-cols-2 gap-2">
                {/* Most Vulnerable */}
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="text-white text-xs font-medium">
                    Most Vulnerable
                  </p>
                  <ul className="text-slate-300 text-xs mt-1">
                    <li>Physical Capital</li>
                    <li>Spiritual Capital</li>
                  </ul>
                </div>

                {/* Most Resilient */}
                <div className="bg-slate-800/50 p-2 rounded">
                  <p className="text-white text-xs font-medium">
                    Most Resilient
                  </p>
                  <ul className="text-slate-300 text-xs mt-1">
                    <li>General Capital</li>
                    <li>Individual Capital</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ─────────────────── Recovery Timeline Milestones (SAFE VERSION) ─────────────────── */}
            <div className="bg-slate-900/50 p-4 rounded-lg">
              <h4 className="text-white font-semibold mb-2">
                When Do Relapses Occur? (Distribution of All Relapses)
              </h4>

              <div className="space-y-3">
                {[
                  {
                    timePoints: ["0-90 days"],
                    label: "Highest risk period", // 36.4% - this is where most relapses happen
                    color: "bg-red-500",
                  },
                  {
                    timePoints: ["91-180 days"],
                    label: "Secondary risk period", // 27.3% - second highest
                    color: "bg-orange-500", // Changed from amber to orange since it's still high risk
                  },
                  {
                    timePoints: ["181-270 days"],
                    label: "Moderate risk period", // 18.2% - moderate risk
                    color: "bg-amber-500", // Moved amber here
                  },
                  {
                    timePoints: ["271-365 days"],
                    label: "Lower risk period", // 9.1% - much lower
                    color: "bg-yellow-500",
                  },
                  {
                    timePoints: ["366-540 days", "541-730 days"],
                    label: "Stabilization period", // 9.1% combined - low and stable
                    color: "bg-green-500",
                  },
                  {
                    timePoints: ["731+ days"],
                    label: "Recovery stability", // 0.0% - no relapses at this stage
                    color: "bg-blue-500",
                  },
                ].map((milestone, index) => {
                  // Calculate percentage of total relapses that occurred in this phase
                  let totalRelapsesInPhase = 0;

                  milestone.timePoints.forEach((timePoint) => {
                    const relapseCount =
                      relapseDistributionData[timePoint] || 0;
                    totalRelapsesInPhase += relapseCount;
                  });

                  const percentageOfAllRelapses =
                    totalRelapsesCount > 0
                      ? (
                          (totalRelapsesInPhase / totalRelapsesCount) *
                          100
                        ).toFixed(1)
                      : 0;

                  // Create display text
                  const displayTime =
                    milestone.timePoints.length === 1
                      ? formatTimePoint(milestone.timePoints[0])
                      : milestone.timePoints[0].includes("366")
                      ? "1-2 years"
                      : "24m+";

                  return (
                    <div key={index} className="flex items-center">
                      <div
                        className={`h-4 w-4 rounded-full ${milestone.color} flex-shrink-0`}
                      ></div>
                      <div className="h-0.5 w-10 bg-slate-600"></div>
                      <div className="bg-slate-800 px-3 py-1 rounded text-xs text-white">
                        {displayTime}: {milestone.label} (
                        {percentageOfAllRelapses}% of all relapses, n=
                        {totalRelapsesInPhase})
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 text-xs text-slate-400">
                Total relapses tracked: {totalRelapsesCount}
              </div>

              {/* Add key insight */}
              <div className="mt-3 text-xs text-blue-300 bg-blue-900/20 p-2 rounded">
                <strong>Key Insight:</strong> Nearly 2/3 of all relapses (63.7%)
                occur within the first 6 months, with the highest concentration
                in the first 90 days. After 24 months, relapse risk becomes
                negligible. (6/1/25)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCProgressionTab;
